import { useEffect, useMemo, useState, type MouseEvent } from 'react';
import { SEO } from '@/components/SEO';
import { CheckCard, CheckHistogram } from '@/components/check';
import { API_BASE_URL } from '@/lib/api';
import { track } from '@/lib/analytics';
import {
  RATE_QUIZZES,
  type CheckCard as CheckCardData,
  type RateSlug,
} from '@/lib/check/quizzes';
import { hqFromSatelliteUrl } from '@/hq/urls';
import { harborHomeUrl, harborSatelliteUrl, requireSatellite } from './config';
import { currentSatellitePath } from './detect';
import { trackSatelliteAction } from './analytics';

const QUIZ_BY_APEX: Record<string, RateSlug> = {
  'ratemycoparent.com': 'rate_other',
  'ratemycoparenting.com': 'rate_self',
};

const CROSS_LINK: Record<string, { href: string; label: string }> = {
  'ratemycoparent.com': {
    href: 'https://ratemycoparenting.com/',
    label: 'Rate how you show up',
  },
  'ratemycoparenting.com': {
    href: 'https://ratemycoparent.com/',
    label: 'Rate the other coparent',
  },
};

const nunito = {
  fontFamily:
    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
};

function isModifiedClick(event: MouseEvent<HTMLAnchorElement>): boolean {
  return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
}

function RateLink({
  to,
  children,
  className,
  onClick,
}: {
  to: string;
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}) {
  function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    onClick?.();
    if (!to.startsWith('/') || isModifiedClick(event)) return;
    event.preventDefault();
    if (window.location.pathname !== to) {
      window.history.pushState({}, '', `${to}${window.location.search}`);
      window.dispatchEvent(new PopStateEvent('popstate'));
      window.scrollTo(0, 0);
    }
  }
  return (
    <a href={to} className={className} onClick={handleClick}>
      {children}
    </a>
  );
}

function useSatellitePath(): string {
  const [path, setPath] = useState(currentSatellitePath);
  useEffect(() => {
    const onPop = () => setPath(currentSatellitePath());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  return path;
}

function parseResultId(path: string): string | null {
  const match = path.match(/^\/r\/([0-9a-fA-F-]{8,64})$/);
  return match?.[1] ?? null;
}

export function RateQuizPage({ apex }: { apex: string }) {
  const config = requireSatellite(apex);
  const quizSlug = QUIZ_BY_APEX[apex];
  const quiz = RATE_QUIZZES[quizSlug];
  const path = useSatellitePath();
  const publicId = parseResultId(path);
  const harborTool = harborSatelliteUrl(config);
  const harborHome = harborHomeUrl(config.apex);
  const cross = CROSS_LINK[apex];

  if (!quiz) {
    return <p className='p-6'>Unknown quiz.</p>;
  }

  if (publicId) {
    return (
      <RateResult
        apex={apex}
        configName={config.name}
        title={config.title}
        description={config.description}
        quizSlug={quizSlug}
        publicId={publicId}
        harborTool={harborTool}
        harborHome={harborHome}
        harborCta={config.harborCta}
        cross={cross}
      />
    );
  }

  return (
    <RateQuizFlow
      apex={apex}
      configName={config.name}
      title={config.title}
      description={config.description}
      tagline={config.tagline}
      quizSlug={quizSlug}
      harborHome={harborHome}
      faqs={config.faqs}
      cross={cross}
    />
  );
}

function RateQuizFlow({
  apex,
  configName,
  title,
  description,
  tagline,
  quizSlug,
  harborHome,
  faqs,
  cross,
}: {
  apex: string;
  configName: string;
  title: string;
  description: string;
  tagline: string;
  quizSlug: RateSlug;
  harborHome: string;
  faqs: { question: string; answer: string }[];
  cross?: { href: string; label: string };
}) {
  const quiz = RATE_QUIZZES[quizSlug];
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const question = quiz.questions[step];
  const total = quiz.questions.length;

  useEffect(() => {
    track('Rate Quiz Screen', { host: apex, quiz: quizSlug, step: question.key });
  }, [apex, quizSlug, question.key]);

  const submit = async (nextAnswers: Record<string, string>) => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/v1/checks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quiz_slug: quizSlug, answers: nextAnswers }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { detail?: string };
        throw new Error(body.detail || 'Could not save your check.');
      }
      const data = (await res.json()) as { public_id: string };
      const search = window.location.search;
      window.history.pushState({}, '', `/r/${data.public_id}${search}`);
      window.dispatchEvent(new PopStateEvent('popstate'));
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save your check.');
      setSubmitting(false);
    }
  };

  const onPick = (key: string) => {
    const next = { ...answers, [question.key]: key };
    setAnswers(next);
    track('Rate Question Answered', {
      host: apex,
      quiz: quizSlug,
      question: question.key,
      answer: key,
    });
    if (step + 1 < total) {
      setStep(step + 1);
      return;
    }
    void submit(next);
  };

  return (
    <RateShell
      apex={apex}
      name={configName}
      title={title}
      description={description}
      path='/'
      harborHome={harborHome}
      cross={cross}
    >
      <p className='text-xs font-semibold uppercase tracking-wide text-primary' style={nunito}>
        {step === 0 ? 'Before you start' : configName}
      </p>
      <p className='mt-2 text-sm text-gray-500' style={nunito}>
        {step + 1} / {total}
      </p>
      <h1 className='mt-2 text-3xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
        {step === 0 ? 'First, a bit about you' : question.prompt}
      </h1>
      {step === 0 ? (
        <p className='mt-2 text-gray-600' style={nunito}>
          {tagline}
        </p>
      ) : null}
      {step > 0 ? (
        <button
          type='button'
          className='mt-4 text-sm font-semibold text-primary'
          style={nunito}
          onClick={() => setStep(step - 1)}
        >
          Back
        </button>
      ) : (
        <p className='mt-6 text-lg font-semibold text-gray-900' style={nunito}>
          {question.prompt}
        </p>
      )}
      <div className='mt-4 grid gap-3'>
        {question.options.map((opt) => {
          const selected = answers[question.key] === opt.key;
          return (
            <button
              key={opt.key}
              type='button'
              disabled={submitting}
              onClick={() => onPick(opt.key)}
              className={`rounded-2xl border px-4 py-4 text-left text-lg font-semibold transition-colors ${
                selected
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-gray-200 bg-gray-50 text-gray-900 hover:border-primary/40'
              }`}
              style={nunito}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
      {error ? (
        <p className='mt-4 text-sm text-red-600' style={nunito}>
          {error}
        </p>
      ) : null}

      <section className='mt-12'>
        <h2 className='text-xl font-semibold'>Questions</h2>
        <dl className='mt-4 space-y-5'>
          {faqs.map((faq) => (
            <div key={faq.question}>
              <dt className='font-medium'>{faq.question}</dt>
              <dd className='mt-1 text-gray-600'>{faq.answer}</dd>
            </div>
          ))}
        </dl>
      </section>
    </RateShell>
  );
}

function RateResult({
  apex,
  configName,
  title,
  description,
  quizSlug,
  publicId,
  harborTool,
  harborHome,
  harborCta,
  cross,
}: {
  apex: string;
  configName: string;
  title: string;
  description: string;
  quizSlug: RateSlug;
  publicId: string;
  harborTool: string;
  harborHome: string;
  harborCta: string;
  cross?: { href: string; label: string };
}) {
  const [card, setCard] = useState<CheckCardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [shareLabel, setShareLabel] = useState('Share your result');
  const shareUrl = `https://${apex}/r/${publicId}`;
  const ogImage = `${API_BASE_URL || `https://${apex}`}/v1/checks/${publicId}/og.png`;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/v1/checks/${publicId}`);
        if (!res.ok) throw new Error('This result is gone.');
        const data = (await res.json()) as { card: CheckCardData };
        if (data.card.quiz_slug !== quizSlug) {
          throw new Error('This result is for a different check.');
        }
        if (!cancelled) setCard(data.card);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not load this result.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [publicId, quizSlug]);

  useEffect(() => {
    if (!card) return;
    track('Rate Result View', {
      host: apex,
      quiz: quizSlug,
      public_id: publicId,
      type: card.type_key,
    });
  }, [apex, card, publicId, quizSlug]);

  const onShare = async () => {
    track('Rate Share Tapped', { host: apex, quiz: quizSlug, public_id: publicId });
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareLabel('Link copied');
      window.setTimeout(() => setShareLabel('Share your result'), 2500);
    } catch {
      setShareLabel('Copy the link from the address bar');
    }
    try {
      if (navigator.share) {
        await navigator.share({
          title: card?.og_title || configName,
          text: card?.identity_line || 'See the card.',
          url: shareUrl,
        });
      }
    } catch {
      // canceled
    }
  };

  const ogAbs = ogImage.startsWith('http') ? ogImage : `https://${apex}${ogImage}`;

  return (
    <RateShell
      apex={apex}
      name={configName}
      title={card?.og_title || title}
      description={card?.og_description || description}
      path={`/r/${publicId}`}
      harborHome={harborHome}
      cross={cross}
      ogImage={ogAbs}
      noindex
    >
      {error ? (
        <p className='text-center text-gray-600' style={nunito}>
          {error}{' '}
          <RateLink to='/' className='font-semibold text-primary'>
            Check yours
          </RateLink>
        </p>
      ) : null}
      {card ? (
        <>
          <CheckCard card={card} />
          <CheckHistogram card={card} />
          <div className='mt-8 grid gap-3'>
            <button
              type='button'
              onClick={() => void onShare()}
              className='rounded-2xl bg-primary px-6 py-4 text-lg font-semibold text-white shadow-sm hover:bg-primary-hover'
              style={nunito}
            >
              {shareLabel}
            </button>
            <RateLink
              to='/'
              onClick={() => track('Rate Yours Tapped', { host: apex, quiz: quizSlug })}
              className='rounded-2xl border border-primary px-6 py-4 text-center text-lg font-semibold text-primary'
            >
              Check yours
            </RateLink>
            <a
              href={harborTool}
              className='pt-2 text-center text-sm font-semibold text-gray-600 underline-offset-2 hover:underline'
              style={nunito}
            >
              {card.cta_label || harborCta}
            </a>
          </div>
          <p className='mt-8 text-center text-xs text-gray-500' style={nunito}>
            Educational, not legal advice. No names stored.
          </p>
        </>
      ) : !error ? (
        <p className='text-center text-gray-500' style={nunito}>
          Loading your card…
        </p>
      ) : null}
    </RateShell>
  );
}

function RateShell({
  apex,
  name,
  title,
  description,
  path,
  harborHome,
  cross,
  ogImage,
  noindex,
  children,
}: {
  apex: string;
  name: string;
  title: string;
  description: string;
  path: string;
  harborHome: string;
  cross?: { href: string; label: string };
  ogImage?: string;
  noindex?: boolean;
  children: React.ReactNode;
}) {
  const config = requireSatellite(apex);
  const jsonLd = useMemo(
    () =>
      path === '/'
        ? [
            {
              '@context': 'https://schema.org',
              '@type': 'WebApplication',
              name: config.name,
              url: `https://${apex}/`,
              applicationCategory: 'LifestyleApplication',
              operatingSystem: 'Web',
              description: config.description,
              publisher: {
                '@type': 'Organization',
                name: 'Harbor',
                url: 'https://harborcoparent.com',
              },
              offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
            },
          ]
        : undefined,
    [apex, config.description, config.name, path],
  );

  return (
    <div className='min-h-screen bg-white text-gray-900'>
      <SEO
        title={title}
        description={description}
        path={path}
        baseUrl={`https://${apex}`}
        siteName={name}
        ogImage={ogImage}
        noindex={noindex}
        jsonLd={jsonLd}
      />
      <header className='border-b border-gray-100'>
        <div className='mx-auto flex max-w-lg items-center justify-between px-4 py-4'>
          <RateLink to='/' className='text-lg font-semibold tracking-tight'>
            {name}
          </RateLink>
          <a
            href={harborHome}
            className='text-sm text-primary hover:underline'
            onClick={() =>
              trackSatelliteAction('Satellite Harbor CTA Pressed', config, { location: 'header' })
            }
          >
            Built by Harbor
          </a>
        </div>
      </header>
      <main className='mx-auto max-w-lg px-4 py-10'>{children}</main>
      <footer className='border-t border-gray-100'>
        <div className='mx-auto max-w-lg space-y-2 px-4 py-8 text-sm text-gray-500'>
          <p>
            Built by the team behind{' '}
            <a
              href={harborHome}
              className='text-primary hover:underline'
              onClick={() =>
                trackSatelliteAction('Satellite Harbor CTA Pressed', config, { location: 'footer' })
              }
            >
              Harbor
            </a>
            . {name} is a free standalone quiz on {apex}.
          </p>
          {cross ? (
            <p>
              <a href={cross.href} className='text-primary hover:underline'>
                {cross.label}
              </a>
            </p>
          ) : null}
          <p>
            More coparent tools at{' '}
            <a href={hqFromSatelliteUrl(apex)} className='text-primary hover:underline'>
              Co-Parent HQ
            </a>
            .
          </p>
        </div>
      </footer>
    </div>
  );
}
